import React from "react";
import { useUrlFriendlyToParameters } from "helpers/urlFriendly";
import { useQueries, useQuery } from "@tanstack/react-query";
import { getScheduleData, getScheduleDays } from "data/Querys";
import Loader from "components/general/Loader";
import NotAvailable from "components/common/NotAvailable";
import SessionScheduleTable from "components/common/schedule/SessionScheduleTable";
import { ALL_URL, groupByDate } from "helpers/utils";
import { useGetConfigSports } from "helpers/commonCodes";
import DateScheduleTable from "components/common/schedule/DateScheduleTable";
import { useSelector } from "react-redux";
import SelectorDay from "components/common/selector-day/SelectorDay";
import DayList from 'components/common/selector-day/Day';
import useViewport from "components/general/useViewPort";
import SelectorTimeZone from "components/common/selectors/selector-timezone";
import { LegendProvider } from "components/common/legendContext";
import { CleanLegends } from "components/common/CleanLegends";
import { LegendsBox } from "components/common/LegendsBox";
import Label from "components/general/Label";

const ScheduleDiscipline = () => {
  const { mirs_competition, mirs_language, mirs_discipline, mirs_event } =
    useUrlFriendlyToParameters();
  const { breakPoint } = useViewport();
  const dataConfigSport = useGetConfigSports({ mirs_competition, mirs_discipline });
  const timezone = useSelector((state) => state.appGlobal.timezone)
  const isH2H = dataConfigSport?.isH2H;
  const { data: daysData, isError: isErrorScheduleDays, isLoading: isLoadingScheduleDays, } = useQuery(
    {
      queryKey: ['scheduleDays', mirs_competition, mirs_language, mirs_discipline],
      queryFn: getScheduleDays,
      refetchInterval: 30000,
    });

  let queryArray = [];
  daysData?.competition_schedule?.forEach((item) => {
    queryArray.push({
      queryKey: ['scheduleData', mirs_competition, mirs_language, mirs_discipline, item.date, mirs_event, isH2H],
      queryFn: getScheduleData,
      refetchInterval: 30000,
      refetchIntervalInBackground: true,
    })
  })

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const results = useQueries({ queries: queryArray });
  const isLoading = isLoadingScheduleDays || results.some(result => result.isLoading)
  const isError = isErrorScheduleDays || results.some(result => result.isError)
  const allSuccess = results.every(result => result.isSuccess === true)

  if (isError) { return <NotAvailable />; }
  if (isLoading) { return <Loader /> }
  if (allSuccess) {
    let items = []
    results && results.forEach((item) => {
      const data = item.data.filter((u) => u?.eventUnit?.phase?.type?.match('[13]'));
      items.push(...data)
    });

    //filters by event
    if (mirs_event !== undefined && mirs_event !== ALL_URL.ALL_EVENT) {
      items = items?.filter(row => filterEvent({ item: row?.eventUnit?.phase?.event?.code, event: mirs_event }))
    }


    //Group data by days whit timezone, structure object day in key , schedules by day in value
    let dates = groupByDate(items, 'startDate', timezone);
    let itemsDates = { competition_schedule: [] };

    for (const key in dates) {
      itemsDates.competition_schedule.push({ date: key })
    }

    const daysSchedule = (Object.keys(dates).map((day => {
      const scheduleData = dates[day];
      const discipline = scheduleData[0]?.discipline?.code;
      const eventorder = scheduleData[0]?.discipline?.eventorder;

      let schedule = null;

      if (scheduleData?.length > 0) {
        switch (eventorder) {
          case 'DATE':
          case 'LOC':
            schedule = <DateScheduleTable data={scheduleData} eventorder={eventorder} key={`schedule-${discipline}-${day}`} isH2H={isH2H} />
            break;
          case 'SESSION':
            schedule = <SessionScheduleTable data={scheduleData} key={`schedule-${discipline}-${day}`} isH2H={isH2H} />
            break;
          default:
            schedule = <DateScheduleTable data={scheduleData} eventorder={eventorder} key={`schedule-${discipline}-${day}`} isH2H={isH2H} />
            break;
        }
      } else {
        schedule = null;
      }
      return schedule;
    })));
    let isBreaked = breakPoint === "md" || breakPoint === "sm" || breakPoint === "xs";
    return (<>
      <nav className='selectors-container' direction={isBreaked ? "horizontal" : "vertical"} aria-labelledby="NavScheduleDiscipline">
        <span id="NavScheduleDiscipline" className="d-none"><Label data="LabelScheduleDiscNav" /></span>
        {
          (isBreaked)
            ? <>
              <SelectorTimeZone />
              <SelectorDay daysItems={itemsDates} />
            </>
            :
            <>
              <DayList daysItems={itemsDates} />
              <div className="d-flex justify-content-end">
                <SelectorTimeZone />
              </div>
            </>
        }
        {/**
        <div className="d-flex">
          <SelectorDiscipline display_type={SELECTOR_DISPLAY_TYPE.COMBO} page="schedule" />
          <SelectorEvent page='schedule' />
        </div>
         */}
      </nav>
      <LegendProvider >
        <CleanLegends />
        {daysSchedule}
        <LegendsBox />
      </LegendProvider>

    </>
    );
  };
}

const filterEvent = ({ item, event }) => {
  if (event === 'EQUOEVEN--------------') {
    return item.startsWith('EQUOEVEN')
  } else if (event === 'EQUODRES--------------') {
    return item.startsWith('EQUODRES')
  } else if (event === 'EQUOJUMP--------------') {
    return item.startsWith('EQUODRES')
  }
  return item === event
}

export default ScheduleDiscipline;
